import {
    Button,
    defaultIconSize,
    type Awaitable,
    type ExpandedState,
    type IId,
    type ITableProperties,
} from '@synqo/frontend-component-library';
import { EnumFrontendLayoutSetting } from '@Model';
import { useCallback, useState } from 'react';
import { TableWithLayoutSettings } from './TableWithLayoutSettings';
import { useToolbarElements, type ActionsProps, type ToolbarElements, type ToolbarOption } from './useToolbarElements';
import { IconPlus } from '@tabler/icons-react';
import { buildPositionElementId, useScrollToId } from '@Hooks/index';

const defaultToolbarElements: ToolbarOption[] = ['add', 'delete'];

export interface PositionsTableProps<TItem extends IId>
    extends Omit<ITableProperties<TItem>, 'toolbarElements' | 'onAdd'>,
        ActionsProps {
    expanded?: ExpandedState;
    toolbarElements?: ToolbarElements;
    editable?: boolean;
    onAdd: () => Awaitable<TItem | undefined | void>;
    onDelete?: (deletedIds: string[]) => Awaitable<unknown>;
    layoutSettingsContext: EnumFrontendLayoutSetting;
}

export const PositionsTable = <TItem extends IId>({
    items,
    expanded: expandedItems = {},
    toolbarElements = defaultToolbarElements,
    editable = false,
    onAdd,
    onDelete,
    addActionProps,
    deleteActionProps,
    ...props
}: PositionsTableProps<TItem>) => {
    const [expandedRowIds, setExpandedRowIds] = useState<ExpandedState>(expandedItems);
    const [selectedObjectIds, setSelectedObjectIds] = useState<string[]>([]);
    const { scrollToId } = useScrollToId(buildPositionElementId);

    const { canCreate } = props;
    const onAddHandler = useCallback(async () => {
        if (!canCreate || !editable) {
            return false;
        }

        const pos = await onAdd();
        if (!pos) {
            return false;
        }

        scrollToId(pos.id);

        setExpandedRowIds((expanded) => (expanded === true ? expanded : { ...expanded, [pos.id]: true }));

        return true;
    }, [editable, onAdd, canCreate, scrollToId]);

    const onDeleteHandler = useCallback(() => {
        if (!editable) {
            return false;
        }

        setSelectedObjectIds((deletedIds) => {
            onDelete?.(deletedIds);
            return [];
        });
    }, [editable, onDelete]);

    const toolbarEms = useToolbarElements({
        toolbarElements,
        onAddClick: onAddHandler,
        onDeleteClick: onDeleteHandler,
        addIsLocked: !editable,
        deleteIsLocked: !editable || selectedObjectIds.length <= 0,
        addActionProps,
        deleteActionProps,
        selectedIds: selectedObjectIds,
    });

    if (!editable && selectedObjectIds.length > 0) {
        setSelectedObjectIds([]);
    }

    return (
        <>
            <TableWithLayoutSettings
                items={items}
                initialDensity="xs"
                forceExpand={{
                    expanded: expandedRowIds,
                    setExpanded: setExpandedRowIds,
                }}
                objectSelector={{
                    selectedObjectIds,
                    setSelectedObjectIds,
                    locked: !editable,
                }}
                onAdd={onAddHandler}
                toolbarElements={toolbarEms}
                {...props}
                canCreate={editable && canCreate}
            />
            {editable && items && items.length > 0 && (
                <Button onClick={onAddHandler} appearance="outlined" fullWidth>
                    <IconPlus size={defaultIconSize} />
                    &nbsp;{addActionProps?.text ?? 'Position hinzufügen'}
                </Button>
            )}
        </>
    );
};
